import React, { useCallback, useMemo } from 'react'
import { Currency } from '@sequencehq/api'
import {
  GreyGrey10,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  IndigoIndigo50,
  IconChevronRight,
  IconChevronDown,
  IconPlay,
  Spinner,
  Box,
  Flex
} from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { useAtom } from 'jotai'
import { TableRow, expansionModeAtom } from './groupingEngine'
import { match } from 'ts-pattern'
import { CustomerPreviewCardPillStatic } from 'Customer/components/CustomerPreviewCard'
import { ProductLinkCell } from 'RevenueRecognition/view/common/ProductLinkCell'
import { RevenueWaterfallSortDirection, YearMonth } from './types'
import { format } from '@sequencehq/utils/dates'

const EMPTY_SELECTION_BORDERS = {
  top: false,
  right: false,
  bottom: false,
  left: false
}

const HEADER_ICON_BUTTON_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer'
}

const BASE_FOCUS_STYLE_FOCUSED: React.CSSProperties = {
  outline: `2px solid ${IndigoIndigo50}`,
  outlineOffset: '-2px',
  backgroundColor: GreyGrey10
}

const BASE_FOCUS_STYLE_UNFOCUSED: React.CSSProperties = {
  outline: 'none',
  outlineOffset: '0px',
  backgroundColor: 'transparent'
}

const formatMoney = (
  value: number | undefined,
  currency: Currency,
  fallback = ''
) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback
  }

  return toMoney({
    value: value.toString(),
    currency
  })
}

// Cache for selection styles, otherwise objects would get recreated
const selectionStyleCache = new Map<string, React.CSSProperties>()

const getSelectionStyleKey = (
  isFocused: boolean,
  isSelected: boolean,
  borders: typeof EMPTY_SELECTION_BORDERS
) => {
  if (!isSelected || isFocused) return null
  return `${borders.top ? '1' : '0'}${borders.right ? '1' : '0'}${borders.bottom ? '1' : '0'}${borders.left ? '1' : '0'}`
}

export const getCellFocusStyle = (
  isFocused: boolean,
  isSelected: boolean = false,
  selectionBorders: {
    top: boolean
    right: boolean
    bottom: boolean
    left: boolean
  } = EMPTY_SELECTION_BORDERS
): React.CSSProperties => {
  // Fast path for focused cells
  if (isFocused) {
    return BASE_FOCUS_STYLE_FOCUSED
  }

  // Fast path for unselected cells
  if (!isSelected) {
    return BASE_FOCUS_STYLE_UNFOCUSED
  }

  // Check cache for selected styles
  const cacheKey = getSelectionStyleKey(isFocused, isSelected, selectionBorders)
  if (cacheKey) {
    const cached = selectionStyleCache.get(cacheKey)
    if (cached) return cached
  }

  // Build style for selected cell
  const shadows = []
  if (selectionBorders.top) shadows.push(`inset 0 2px 0 0 ${IndigoIndigo50}`)
  if (selectionBorders.right) shadows.push(`inset -2px 0 0 0 ${IndigoIndigo50}`)
  if (selectionBorders.bottom)
    shadows.push(`inset 0 -2px 0 0 ${IndigoIndigo50}`)
  if (selectionBorders.left) shadows.push(`inset 2px 0 0 0 ${IndigoIndigo50}`)

  const style: React.CSSProperties = {
    outline: 'none',
    outlineOffset: '0px',
    backgroundColor: GreyGrey10,
    boxShadow: shadows.length > 0 ? shadows.join(', ') : 'none',
    position: 'relative',
    zIndex: 2
  }

  // Cache it
  if (cacheKey) {
    selectionStyleCache.set(cacheKey, style)
  }

  return style
}

const baseDataCellStyle = (rowIndex: number): React.CSSProperties => ({
  textAlign: 'right',
  padding: '8px 12px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  borderTop: rowIndex !== 0 ? `1px solid ${GreyGrey30}` : 'none',
  color: GreyGrey80,
  fontSize: '13px',
  lineHeight: '16px'
})

const baseSummaryCellStyle: React.CSSProperties = {
  height: '32px',
  borderTop: `1px solid ${GreyGrey30}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '8px 12px',
  fontSize: '12px',
  fontWeight: 'semibold',
  color: '#4A5568'
}

const baseGroupCellStyle = (rowIndex: number): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  height: '32px',
  borderTop: rowIndex !== 0 ? `1px solid ${GreyGrey30}` : 'none',
  padding: '8px 12px'
})

const SortChevrons = ({
  direction
}: {
  direction: RevenueWaterfallSortDirection | undefined
}) => {
  return (
    <Flex flexDirection="column">
      <Box transform="rotate(-90deg)">
        <IconPlay
          size={8}
          color={direction === 'ASC' ? '#2B6CB0' : '#A0AEC0'}
          opacity={direction === 'ASC' ? 1 : 0.5}
        />
      </Box>
      <Box transform="rotate(90deg)">
        <IconPlay
          size={8}
          color={direction === 'DESC' ? '#2B6CB0' : '#A0AEC0'}
          opacity={direction === 'DESC' ? 1 : 0.5}
        />
      </Box>
    </Flex>
  )
}

const BaseHeaderCell = ({
  children,
  sortable = false,
  active = false,
  sortDirection,
  onSort,
  align = 'left',
  showSpinner = false,
  ...boxProps
}: {
  children: React.ReactNode
  sortable?: boolean
  active?: boolean
  sortDirection?: RevenueWaterfallSortDirection
  onSort?: () => void
  align?: 'left' | 'right'
  showSpinner?: boolean
} & React.ComponentProps<typeof Box>) => {
  const renderSortIndicator = () => {
    if (!sortable) return null

    return (
      <Flex align="center" gap="4px" ml="4px">
        <SortChevrons direction={active ? sortDirection : undefined} />
        {showSpinner ? (
          <Spinner size="xs" color="#718096" thickness="2px" speed="0.6s" />
        ) : null}
      </Flex>
    )
  }

  const indicator = renderSortIndicator()

  return (
    <Box
      height="32px"
      bg={GreyGrey10}
      display="flex"
      alignItems="center"
      justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
      padding="8px 12px"
      fontSize="12px"
      fontWeight="semibold"
      color="#718096"
      cursor={sortable ? 'pointer' : 'default'}
      userSelect={sortable ? 'none' : 'auto'}
      onClick={sortable ? onSort : undefined}
      _hover={sortable ? { backgroundColor: '#EDF2F7' } : {}}
      {...boxProps}
    >
      <Flex
        align="center"
        justify={align === 'right' ? 'flex-end' : 'flex-start'}
        width="100%"
        gap="4px"
      >
        {children}
        {indicator}
      </Flex>
    </Box>
  )
}

export const GroupHeaderCell = ({
  primaryGroup,
  sortable = false,
  active = false,
  sortDirection,
  onSort,
  showSpinner = false
}: {
  primaryGroup?: string
  sortable?: boolean
  active?: boolean
  sortDirection?: RevenueWaterfallSortDirection
  onSort?: () => void
  showSpinner?: boolean
}) => {
  const [expansionMode, setExpansionMode] = useAtom(expansionModeAtom)

  const getGroupingHeaderText = (grouping?: string): string => {
    switch (grouping) {
      case 'CUSTOMER':
        return 'Customers'
      case 'PRODUCT':
        return 'Products'
      case 'MONTH':
        return 'Months'
      case 'ACCOUNT':
        return 'Accounts'
      case 'XERO_CODE':
        return 'Xero codes'
      default:
        return 'Group'
    }
  }

  return (
    <BaseHeaderCell
      borderRight={`1px solid ${GreyGrey30}`}
      borderBottom={`1px solid ${GreyGrey30}`}
      sortable={sortable}
      active={active}
      sortDirection={sortDirection}
      onSort={onSort}
      showSpinner={showSpinner}
      align="left"
    >
      <Flex
        align="center"
        width="100%"
        gap="8px"
        data-testid="revrec.waterfall.table.header.group"
      >
        <Box
          as="button"
          type="button"
          ml="-2px"
          aria-label={
            expansionMode === 'all'
              ? 'Collapse all groups'
              : 'Expand all groups'
          }
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation()
            event.preventDefault()
            if (expansionMode === 'all') {
              setExpansionMode('none')
            } else {
              setExpansionMode('all')
            }
          }}
          onMouseDown={(event: React.MouseEvent) => {
            event.stopPropagation()
          }}
          style={HEADER_ICON_BUTTON_STYLE}
          data-testid="revrec.waterfall.table.header.toggleExpansion"
        >
          {expansionMode === 'all' ? (
            <IconChevronDown color={GreyGrey60} />
          ) : (
            <IconChevronRight color={GreyGrey60} />
          )}
        </Box>
        <Box flexShrink={0}>{getGroupingHeaderText(primaryGroup)}</Box>
      </Flex>
    </BaseHeaderCell>
  )
}

export const TotalHeaderCell = ({
  sortable = false,
  active = false,
  sortDirection,
  onSort,
  showSpinner = false
}: {
  sortable?: boolean
  active?: boolean
  sortDirection?: RevenueWaterfallSortDirection
  onSort?: () => void
  showSpinner?: boolean
}) => (
  <BaseHeaderCell
    borderRight={`1px solid ${GreyGrey30}`}
    borderBottom={`1px solid ${GreyGrey30}`}
    sortable={sortable}
    active={active}
    sortDirection={sortDirection}
    onSort={onSort}
    showSpinner={showSpinner}
    align="right"
  >
    Total
  </BaseHeaderCell>
)

export const formatYearMonthToDisplay = (yearMonth: YearMonth): string => {
  const [year, month] = yearMonth.split('-')
  const yearNumber = Number(year)
  const monthIndex = Number(month) - 1
  const date = new Date(Date.UTC(yearNumber, monthIndex, 1))
  return format(date, 'MMM yyyy')
}

export const MonthHeaderCell = ({
  month,
  isLast,
  sortable = false,
  active = false,
  sortDirection,
  onSort,
  showSpinner = false
}: {
  month: YearMonth
  isLast: boolean
  sortable?: boolean
  active?: boolean
  sortDirection?: RevenueWaterfallSortDirection
  onSort?: () => void
  showSpinner?: boolean
}) => (
  <BaseHeaderCell
    borderRight={!isLast ? `1px solid ${GreyGrey30}` : 'none'}
    borderBottom={`1px solid ${GreyGrey30}`}
    sortable={sortable}
    active={active}
    sortDirection={sortDirection}
    onSort={onSort}
    showSpinner={showSpinner}
    align="right"
  >
    {formatYearMonthToDisplay(month)}
  </BaseHeaderCell>
)

export const RecognizedHeaderCell = ({
  sortable = false,
  active = false,
  sortDirection,
  onSort,
  showSpinner = false
}: {
  sortable?: boolean
  active?: boolean
  sortDirection?: RevenueWaterfallSortDirection
  onSort?: () => void
  showSpinner?: boolean
}) => (
  <BaseHeaderCell
    borderBottom={`1px solid ${GreyGrey30}`}
    sortable={sortable}
    active={active}
    sortDirection={sortDirection}
    onSort={onSort}
    showSpinner={showSpinner}
    align="right"
  >
    Recognized
  </BaseHeaderCell>
)

export const RemainingHeaderCell = ({
  sortable = false,
  active = false,
  sortDirection,
  onSort,
  showSpinner = false
}: {
  sortable?: boolean
  active?: boolean
  sortDirection?: RevenueWaterfallSortDirection
  onSort?: () => void
  showSpinner?: boolean
}) => (
  <BaseHeaderCell
    borderBottom={`1px solid ${GreyGrey30}`}
    sortable={sortable}
    active={active}
    sortDirection={sortDirection}
    onSort={onSort}
    showSpinner={showSpinner}
    align="right"
  >
    Remaining
  </BaseHeaderCell>
)

const DataLabel = ({
  row,
  isChildRow
}: {
  row: TableRow
  isChildRow: boolean
}) => {
  const fontWeight = isChildRow ? 'normal' : '600'

  return match(row.type)
    .with('customer', () => {
      const customerId = row.customerId ?? row.id

      if (!customerId) {
        return (
          <div
            style={{
              color: GreyGrey80,
              fontWeight,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '13px',
              lineHeight: '16px',
              padding: '4px 0px',
              margin: 0
            }}
          >
            {row.label}
          </div>
        )
      }

      const pill = (
        <CustomerPreviewCardPillStatic
          variant="ghost"
          customerId={customerId}
          customerName={row.label}
        />
      )

      return isChildRow ? pill : <Flex width="90%">{pill}</Flex>
    })
    .with('product', () => {
      const productId = row.productId ?? row.id

      if (!productId) {
        return (
          <div
            style={{
              color: GreyGrey80,
              fontWeight,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '13px',
              lineHeight: '16px',
              padding: '6px 12px',
              margin: 0,
              border: '1px solid transparent',
              borderRadius: '4px',
              display: 'inline-block',
              minHeight: '24px',
              boxSizing: 'border-box'
            }}
          >
            {row.label}
          </div>
        )
      }

      return (
        <ProductLinkCell
          product={{
            id: productId,
            name: row.label
          }}
        />
      )
    })
    .otherwise(() => (
      <div
        style={{
          color: GreyGrey80,
          fontWeight,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '13px',
          lineHeight: '16px'
        }}
      >
        {row.label}
      </div>
    ))
}

export const GroupDataCell = React.memo<{
  rowOriginal: TableRow
  depth: number
  isExpanded: boolean
  rowIndex: number
  isFocused: boolean
  isSelected?: boolean
  selectionBorders?: {
    top: boolean
    right: boolean
    bottom: boolean
    left: boolean
  }
  onToggleExpanded: () => void
  onMouseDown?: (e: React.MouseEvent) => void
  onMouseEnter?: () => void
  onClick?: (e: React.MouseEvent) => void
  onContextMenu?: (e: React.MouseEvent) => void
}>(
  ({
    rowOriginal,
    depth,
    isExpanded,
    rowIndex,
    isFocused,
    isSelected = false,
    selectionBorders = EMPTY_SELECTION_BORDERS,
    onToggleExpanded,
    onMouseDown,
    onMouseEnter,
    onClick,
    onContextMenu
  }: {
    rowOriginal: TableRow
    depth: number
    isExpanded: boolean
    rowIndex: number
    isFocused: boolean
    isSelected?: boolean
    selectionBorders?: {
      top: boolean
      right: boolean
      bottom: boolean
      left: boolean
    }
    onToggleExpanded: () => void
    onMouseDown?: (e: React.MouseEvent) => void
    onMouseEnter?: () => void
    onClick?: (e: React.MouseEvent) => void
    onContextMenu?: (e: React.MouseEvent) => void
  }) => {
    const hasChildren = rowOriginal.children.length > 0
    const isExpandable = hasChildren
    const showChevron = hasChildren
    const isChildRow = rowOriginal.depth > 0
    const indentLevel = depth * 16

    const cellStyle = useMemo(
      () => ({
        ...baseGroupCellStyle(rowIndex),
        gap: '8px',
        paddingLeft: `${12 + indentLevel}px`,
        cursor: isExpandable ? 'pointer' : 'default',
        borderRight: `1px solid ${GreyGrey30}`,
        ...getCellFocusStyle(isFocused, isSelected, selectionBorders)
      }),
      [
        rowIndex,
        indentLevel,
        isExpandable,
        isFocused,
        isSelected,
        selectionBorders
      ]
    )

    const handleChevronClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        onToggleExpanded()
      },
      [onToggleExpanded]
    )

    const handleCellClick = useCallback(
      (e: React.MouseEvent) => {
        // Only handle selection, not expansion
        onClick?.(e)
      },
      [onClick]
    )

    return (
      <div
        style={cellStyle}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onContextMenu={onContextMenu}
        onClick={handleCellClick}
        data-testid="revrec.waterfall.table.row.cell"
      >
        {showChevron && (
          <div
            style={{
              flexShrink: 0,
              cursor: isExpandable ? 'pointer' : 'default',
              opacity: isExpandable ? 1 : 0.4
            }}
            data-testid="revrec.waterfall.table.row.expand"
            aria-disabled={!isExpandable}
            tabIndex={isExpandable ? 0 : -1}
            onClick={isExpandable ? handleChevronClick : undefined}
          >
            {isExpanded && isExpandable ? (
              <IconChevronDown color={GreyGrey60} />
            ) : (
              <IconChevronRight color={GreyGrey60} />
            )}
          </div>
        )}
        <DataLabel row={rowOriginal} isChildRow={isChildRow} />
      </div>
    )
  },
  (prev, next) => {
    if (
      prev.rowOriginal.id !== next.rowOriginal.id ||
      prev.rowOriginal.label !== next.rowOriginal.label ||
      prev.rowOriginal.type !== next.rowOriginal.type
    ) {
      return false
    }

    const prevHasSubRows = prev.rowOriginal.children.length > 0
    const nextHasSubRows = next.rowOriginal.children.length > 0
    if (prevHasSubRows !== nextHasSubRows) {
      return false
    }

    return (
      prev.depth === next.depth &&
      prev.isExpanded === next.isExpanded &&
      prev.isFocused === next.isFocused &&
      prev.isSelected === next.isSelected &&
      prev.rowIndex === next.rowIndex &&
      prev.selectionBorders?.top === next.selectionBorders?.top &&
      prev.selectionBorders?.right === next.selectionBorders?.right &&
      prev.selectionBorders?.bottom === next.selectionBorders?.bottom &&
      prev.selectionBorders?.left === next.selectionBorders?.left
    )
  }
)

export const TotalDataCell = React.memo(
  ({
    value,
    currency,
    rowIndex,
    isFocused,
    isSelected = false,
    selectionBorders = EMPTY_SELECTION_BORDERS,
    onMouseDown,
    onMouseEnter,
    onClick,
    onContextMenu
  }: {
    value: number
    currency: Currency
    rowIndex: number
    isFocused: boolean
    isSelected?: boolean
    selectionBorders?: {
      top: boolean
      right: boolean
      bottom: boolean
      left: boolean
    }
    onMouseDown?: (e: React.MouseEvent) => void
    onMouseEnter?: () => void
    onClick?: (e: React.MouseEvent) => void
    onContextMenu?: (e: React.MouseEvent) => void
  }) => {
    const displayValue = useMemo(
      () => formatMoney(value, currency),
      [value, currency]
    )
    const cellStyle = useMemo(
      () => ({
        ...baseDataCellStyle(rowIndex),
        borderRight: `1px solid ${GreyGrey30}`,
        ...getCellFocusStyle(isFocused, isSelected, selectionBorders)
      }),
      [rowIndex, isFocused, isSelected, selectionBorders]
    )

    return (
      <div
        style={cellStyle}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        onContextMenu={onContextMenu}
      >
        {displayValue}
      </div>
    )
  }
)

export const MonthDataCell = React.memo(
  ({
    value,
    currency,
    rowIndex,
    isLast,
    isFocused,
    isSelected = false,
    selectionBorders = EMPTY_SELECTION_BORDERS,
    onMouseDown,
    onMouseEnter,
    onClick,
    onContextMenu
  }: {
    value?: number
    currency: Currency
    rowIndex: number
    isLast: boolean
    isFocused: boolean
    isSelected?: boolean
    selectionBorders?: {
      top: boolean
      right: boolean
      bottom: boolean
      left: boolean
    }
    onMouseDown?: (e: React.MouseEvent) => void
    onMouseEnter?: () => void
    onClick?: (e: React.MouseEvent) => void
    onContextMenu?: (e: React.MouseEvent) => void
  }) => {
    const displayValue = useMemo(
      () => (value === undefined ? null : formatMoney(value, currency)),
      [value, currency]
    )
    const cellStyle = useMemo(
      () => ({
        ...baseDataCellStyle(rowIndex),
        borderRight: !isLast ? `1px solid ${GreyGrey30}` : 'none',
        ...getCellFocusStyle(isFocused, isSelected, selectionBorders)
      }),
      [rowIndex, isLast, isFocused, isSelected, selectionBorders]
    )

    return (
      <div
        style={cellStyle}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        onContextMenu={onContextMenu}
      >
        {displayValue}
      </div>
    )
  }
)

export const RecognizedDataCell = React.memo(
  ({
    value,
    currency,
    rowIndex,
    isFocused,
    isSelected = false,
    selectionBorders = EMPTY_SELECTION_BORDERS,
    onMouseDown,
    onMouseEnter,
    onClick,
    onContextMenu
  }: {
    value: number
    currency: Currency
    rowIndex: number
    isFocused: boolean
    isSelected?: boolean
    selectionBorders?: {
      top: boolean
      right: boolean
      bottom: boolean
      left: boolean
    }
    onMouseDown?: (e: React.MouseEvent) => void
    onMouseEnter?: () => void
    onClick?: (e: React.MouseEvent) => void
    onContextMenu?: (e: React.MouseEvent) => void
  }) => {
    const displayValue = useMemo(
      () => formatMoney(value, currency),
      [value, currency]
    )
    const cellStyle = useMemo(
      () => ({
        ...baseDataCellStyle(rowIndex),
        ...getCellFocusStyle(isFocused, isSelected, selectionBorders)
      }),
      [rowIndex, isFocused, isSelected, selectionBorders]
    )

    return (
      <div
        style={cellStyle}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        onContextMenu={onContextMenu}
      >
        {displayValue}
      </div>
    )
  }
)

export const RemainingDataCell = React.memo(
  ({
    value,
    currency,
    rowIndex,
    isFocused,
    isSelected = false,
    selectionBorders = EMPTY_SELECTION_BORDERS,
    onMouseDown,
    onMouseEnter,
    onClick,
    onContextMenu
  }: {
    value: number
    currency: Currency
    rowIndex: number
    isFocused: boolean
    isSelected?: boolean
    selectionBorders?: {
      top: boolean
      right: boolean
      bottom: boolean
      left: boolean
    }
    onMouseDown?: (e: React.MouseEvent) => void
    onMouseEnter?: () => void
    onClick?: (e: React.MouseEvent) => void
    onContextMenu?: (e: React.MouseEvent) => void
  }) => {
    const displayValue = useMemo(
      () => formatMoney(value, currency),
      [value, currency]
    )
    const cellStyle = useMemo(
      () => ({
        ...baseDataCellStyle(rowIndex),
        ...getCellFocusStyle(isFocused, isSelected, selectionBorders)
      }),
      [rowIndex, isFocused, isSelected, selectionBorders]
    )

    return (
      <div
        style={cellStyle}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        onContextMenu={onContextMenu}
      >
        {displayValue}
      </div>
    )
  }
)

// Summary cells
export const TotalSummaryCell = ({
  value,
  currency
}: {
  value: number
  currency: Currency
}) => (
  <div
    style={{
      ...baseSummaryCellStyle,
      backgroundColor: GreyGrey10,
      borderRight: `1px solid ${GreyGrey30}`,
      justifyContent: 'space-between'
    }}
  >
    <div>Total</div>
    <div>{formatMoney(value, currency)}</div>
  </div>
)

export const MonthSummaryCell = ({
  value,
  currency,
  isLast
}: {
  value?: number
  currency: Currency
  isLast: boolean
}) => (
  <div
    style={{
      ...baseSummaryCellStyle,
      backgroundColor: GreyGrey10,
      borderRight: !isLast ? `1px solid ${GreyGrey30}` : 'none'
    }}
  >
    {value === undefined ? null : formatMoney(value, currency)}
  </div>
)

export const RecognizedSummaryCell = ({
  value,
  currency
}: {
  value: number
  currency: Currency
}) => (
  <div
    style={{
      ...baseSummaryCellStyle,
      backgroundColor: GreyGrey10
    }}
  >
    {formatMoney(value, currency)}
  </div>
)

export const RemainingSummaryCell = ({
  value,
  currency
}: {
  value: number
  currency: Currency
}) => (
  <div
    style={{
      ...baseSummaryCellStyle,
      backgroundColor: GreyGrey10
    }}
  >
    {formatMoney(value, currency)}
  </div>
)
